
import React, { createContext, useContext, useCallback, useRef, useEffect, useState } from 'react';
import HandsOnTable from 'handsontable';
import { Subject } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';
import {
    useUpdateShipmentCustomFields,
    useCreateShipment,
    useCreateBulkShipments,
    useDeleteShipment,
    useCreateBulkTrackingJobs,
    useUploadFile,
    useProcessFile,
} from '@/core/api/queries';
import { useCompanyProject } from '../hooks/useCompanyProject';
import { useUIState } from '@/core/UIStateProvider';
import { useShipmentsPolling } from '../hooks/useShipmentsPolling';


interface ChangeEvent {
    id: string;
    index: number;
    field: string;
    newValue: any;
    rowData: any;
    rowIndex: number;
    projectId: string;
    userId: string;
}

interface ShipmentMutationsContextType {
    handleAfterChange: (params: {
        changes: HandsOnTable.CellChange[] | null;
        source: string;
        hotInstance: any;
        projectId: string;
        userId: string;
    }) => void;
    handleAfterPaste: (params: {
        data: any[][];
        coords: any[];
        hotInstance: any;
        columns: any[];
        projectId: string;
        userId: string;
    }) => Promise<void>;
    handleDelete: (params: {
        shipmentId: string;
    }) => Promise<void>;
    handleFileUpload: (params: {
        file: File;
        projectId: string;
    }) => Promise<void>;
    startPolling: (options: { interval: number; maxAttempts: number, projectId: string }) => void;
}

const ShipmentMutationsContext = createContext<ShipmentMutationsContextType | undefined>(undefined);

export const useShipmentMutations = () => {
    const context = useContext(ShipmentMutationsContext);
    if (!context) {
        throw new Error('useShipmentMutations must be used within ShipmentMutationsProvider');
    }
    return context;
};

export const ShipmentMutationsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const { startPolling } = useShipmentsPolling();

    const isSavingRef = useRef<boolean>(false);
    const changeStreamRef = useRef(new Subject<ChangeEvent>());
    const { addNotification } = useUIState();
    const updateShipmentCustomFields = useUpdateShipmentCustomFields();
    const createShipment = useCreateShipment();
    const createBulkShipments = useCreateBulkShipments();
    const deleteShipment = useDeleteShipment();
    const createBulkTrackingJobs = useCreateBulkTrackingJobs();
    const uploadFile = useUploadFile();
    const processFile = useProcessFile();


    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const handleAfterChange = useCallback(
        ({ changes, hotInstance, projectId, userId }: {
            changes: HandsOnTable.CellChange[] | null;
            source: string;
            hotInstance: any;
            projectId: string;
            userId: string;
        }) => {
            if (!changes || changes.length === 0) return;

            if (!isSavingRef.current) {
                isSavingRef.current = true;
                addNotification('info', 'Saving changes...');
            }

            changes.forEach(([index, prop, oldValue, newValue]) => {

                if (oldValue !== newValue && prop !== 'id') {
                    const rowData = hotInstance.getDataAtRow(index);
                    const shipmentId = hotInstance.getDataAtRowProp(index, 'id');

                    changeStreamRef.current.next({
                        id: shipmentId,
                        index,
                        field: prop as string,
                        newValue,
                        rowData,
                        rowIndex: index,
                        projectId,
                        userId,
                    });
                }
            });
        },
        []
    );

    const handleAfterPaste = useCallback(
        async ({ data, coords, hotInstance, columns, projectId, userId }: {
            data: any[][];
            coords: any[];
            hotInstance: any;
            columns: any[];
            projectId: string;
            userId: string;
        }) => {
            if (!hotInstance || !projectId || !userId) return;

            const startRow = coords[0].startRow;
            const startCol = coords[0].startCol;
            const newShipmentsData: any[] = [];

            if (!isSavingRef.current) {
                isSavingRef.current = true;
                addNotification('info', 'Creating shipments...');
            }

            data.forEach((pastedRow, dataRowIndex) => {
                const rowIndex = startRow + dataRowIndex;
                const customFields: Record<string, any> = {};
                const order = rowIndex + 2;

                pastedRow.forEach((cellValue, pastedColIndex) => {
                    const actualColIndex = startCol + pastedColIndex;
                    const column = columns[actualColIndex];

                    if (column && cellValue !== null && cellValue !== undefined && cellValue !== '') {
                        customFields[column.data] = cellValue;
                    }
                });

                if (Object.keys(customFields).length === 0) return;

                newShipmentsData.push({
                    projectId,
                    createdBy: userId,
                    type: 'IMPORT',
                    customFields,
                    order,
                });
            });

            if (newShipmentsData.length > 0) {
                try {
                    const response = await createBulkShipments.mutateAsync({
                        shipments: newShipmentsData,
                    });

                    await createBulkTrackingJobs.mutateAsync(
                        response.successful.map((shipment: any) => ({
                            shipmentId: shipment.id,
                            carrierName: shipment.customFields.carrier,
                            referenceNumber: shipment.customFields.container,
                            referenceType: 'container',
                        }))
                    );

                    addNotification('success', 'Shipments created successfully!');
                } catch (error) {
                    console.error('Failed to create bulk shipments:', error);
                    addNotification('error', 'Failed to create some shipments. Please try again.');
                } finally {
                    isSavingRef.current = false;
                }
            }
        },
        [createBulkShipments, createBulkTrackingJobs]
    );

    const handleDelete = useCallback(
        async ({ shipmentId }: {
            shipmentId: string;
        }) => {
            try {
                await deleteShipment.mutateAsync(shipmentId);
                addNotification('success', 'Shipment deleted successfully!');
            } catch (error) {
                console.error('Failed to delete shipment:', error);
                addNotification('error', 'Failed to delete shipment. Please try again.');
            }
        },
        [deleteShipment]
    );

    const handleFileUpload = useCallback(
        async ({ file, projectId }: {
            file: File;
            projectId: string;
        }) => {
            if (!projectId) {
                addNotification('error', 'Cannot upload: missing project context');
                return;
            }

            const allowedTypes = [
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'application/vnd.ms-excel',
                'text/csv',
            ];
            if (!allowedTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls|csv)$/i)) {
                addNotification('error', 'Please upload only Excel or CSV files');
                return;
            }

            try {
                addNotification('info', 'Uploading file...');
                const { fileRecord } = await uploadFile.mutateAsync({
                    projectId,
                    file,
                });

                addNotification('info', 'Processing file and creating shipments...', 20000);
                await processFile.mutateAsync({
                    fileId: fileRecord.id,
                    data: {},
                });

                startPolling({
                    interval: 5000,
                    maxAttempts: 10,
                    projectId
                });
                addNotification('success', 'File processed! Fetching tracking updates...');
            } catch (error) {
                const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
                addNotification('error', `Upload failed: ${errorMessage}`);
            }
        },
        [uploadFile, processFile]
    );

    // Setup change stream subscription
    useEffect(() => {
        const saveSubscription = changeStreamRef.current
            .pipe(
                debounceTime(1000),
                switchMap(async (change: ChangeEvent) => {


                    try {
                        if (!change.id) {

                            const order = change.rowIndex + 2;
                            const createResponse = await createShipment.mutateAsync({
                                projectId: change.projectId,
                                createdBy: change.userId,
                                type: 'IMPORT',
                                customFields: { [change.field]: change.newValue },
                                order,
                            });
                            //create empty shipment then update it with custom field
                            if (createResponse.id) {

                                await updateShipmentCustomFields.mutateAsync({
                                    shipmentId: createResponse.id,
                                    customFields: { [change.field]: change.newValue },
                                });
                                startPolling({
                                    interval: 3000,
                                    maxAttempts: 3,
                                    projectId: change.projectId
                                });
                            }

                            isSavingRef.current = false;
                            return { success: true, change, newId: createResponse.id };
                        } else {

                            await updateShipmentCustomFields.mutateAsync({
                                shipmentId: change.id,
                                customFields: { [change.field]: change.newValue },
                            });
                            isSavingRef.current = false;
                            startPolling({
                                interval: 3000,
                                maxAttempts: 3,
                                projectId: change.projectId
                            });
                            return { success: true, change };
                        }
                    } catch (error) {
                        console.error('Save failed:', error);
                        return { success: false, change, error };
                    } finally {
                        isSavingRef.current = false;
                    }
                })
            )
            .subscribe();

        return () => saveSubscription.unsubscribe();
    }, []);

    const contextValue: ShipmentMutationsContextType = {
        handleAfterChange,
        handleAfterPaste,
        handleDelete,
        handleFileUpload,
        startPolling
    };

    return (
        <ShipmentMutationsContext.Provider value={contextValue}>
            {children}
        </ShipmentMutationsContext.Provider>
    );
};